import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { checkoutIntentsClient } from 'app/checkout-intents/client';
import { toast } from 'sonner';

type ReturnReasonCode =
  | 'defective_item'
  | 'will_not_arrive_on_time'
  | 'wrong_item_sent'
  | 'better_price_available'
  | 'no_longer_needed'
  | 'other';

const RETURN_REASONS: { code: ReturnReasonCode; label: string }[] = [
  { code: 'defective_item', label: 'Defective Item' },
  { code: 'will_not_arrive_on_time', label: 'Will Not Arrive On Time' },
  { code: 'wrong_item_sent', label: 'Wrong Item Sent' },
  { code: 'better_price_available', label: 'Better Price Available' },
  { code: 'no_longer_needed', label: 'No Longer Needed' },
  { code: 'other', label: 'Other' },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutIntentId: string;
  orderIds?: string[];
  onSuccess?: () => void;
};

export const RequestReturnModal = ({
  open,
  onOpenChange,
  checkoutIntentId,
  orderIds,
  onSuccess,
}: Props) => {
  const [reason, setReason] = useState<ReturnReasonCode | ''>('');
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const hasOrderIds = orderIds && orderIds.length > 0;

  const handleConfirm = async () => {
    if (!reason) {
      return;
    }
    if (hasOrderIds && !selectedOrderId) {
      return;
    }

    const client = checkoutIntentsClient.get();
    if (!client) {
      toast.error('API client not initialized');
      return;
    }

    setSubmitting(true);
    try {
      const selectedReason = RETURN_REASONS.find((r) => r.code === reason);
      await client.post('/api/v1/betas/returns', {
        body: {
          checkoutIntentId,
          ...(selectedOrderId && { orderId: selectedOrderId }),
          reason: {
            code: reason,
            message: selectedReason?.label ?? '',
          },
        },
      });
      toast.success('Return request submitted successfully');
      setReason('');
      setSelectedOrderId('');
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      console.log(err);
      toast.error(err instanceof Error ? err.message : 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setReason('');
      setSelectedOrderId('');
    }
    onOpenChange(value);
  };

  const isConfirmDisabled = !reason || submitting || (hasOrderIds && !selectedOrderId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Return</DialogTitle>
          <DialogDescription>Select a reason for returning this order.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {hasOrderIds && (
            <div className="space-y-2">
              <Label htmlFor="order-id">Order Id</Label>
              <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                <SelectTrigger id="order-id">
                  <SelectValue placeholder="Select an order" />
                </SelectTrigger>
                <SelectContent>
                  {orderIds.map((id) => (
                    <SelectItem key={id} value={id}>
                      {id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="return-reason">Reason</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as ReturnReasonCode)}>
              <SelectTrigger id="return-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {RETURN_REASONS.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            {submitting ? 'Submitting...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
